import { UNAUTHORIZED } from 'http-status-codes';
import { get } from 'lodash';
import { AnyAction } from 'redux';

import { AuthenticatorUser, StoredUserAttributes, UserNamedAttribute } from '@futrli/shared-services';
import { splitOnLastUnderscore } from '@futrli/shared-services';

import {
  CUSTOM_ATTRIBUTES,
  GET_CURRENT_AUTHENTICATED_USER,
  STORE_PREFERRED_MFA_TYPE,
  STORE_UNAUTHENTICATED_USER,
  STORE_USER_ATTRIBUTES,
  UPDATE_CURRENT_AUTHENTICATED_USER
} from '../../auth/constants';
import { _REJECTED } from '../../core/constants';
import { SIGN_OUT } from '../../login/constants';
import { Storage } from '../../storage/storage';

/**
 * Formats the user's attributes in order to be able to merges into the state.
 * @param {UserNamedAttribute[]} attributes
 * @return {StoredUserAttributes} user formatted attributes
 */
const formatUserAttributes = (attributes: UserNamedAttribute[]): StoredUserAttributes => {
  const formattedAttributes: StoredUserAttributes = {
    custom: {},
    email: ''
  };

  attributes.forEach(attribute => {
    if (attribute.Name.startsWith(CUSTOM_ATTRIBUTES.PREFIX)) {
      formattedAttributes.custom[attribute.Name.split(':')[1]] = attribute.Value;
    } else {
      formattedAttributes[attribute.Name] = attribute.Value;
    }
  });

  return formattedAttributes;
};

/**
 * Gets the merged current state with the new formatted attributes.
 * @param {StoredUserAttributes} state
 * @param {AmplifyUserNamedAttribute[]} attributes
 * @return {StoredUserAttributes} user formatted attributes
 */
const mergeUserAttributes = (state: StoredUserAttributes, attributes: UserNamedAttribute[]): StoredUserAttributes => {
  if (!attributes) {
    return state;
  }

  const formattedAttributes = formatUserAttributes(attributes);

  return {
    ...state,
    ...formattedAttributes,
    custom: {
      ...state.custom,
      ...formattedAttributes.custom
    }
  };
};

/**
 * Updates the store state with the new user attributes.
 * @param {StoredUserAttributes} state
 * @param {AnyAction} action
 * @return {StoredUserAttributes} new state
 */
export const userAttributes = (
  state: StoredUserAttributes = { custom: {}, email: '' },
  action: AnyAction
): StoredUserAttributes => {
  switch (action.type) {
    case STORE_USER_ATTRIBUTES:
      return mergeUserAttributes(state, action.payload);
    default:
      return state;
  }
};

/**
 * Updates the store state with the authenticated required info.
 * @param {AuthenticatorUser} state
 * @param {AnyAction} action
 * @return {AuthenticatorUser} new state
 */
export const authenticatedUser = (state: AuthenticatorUser = null, action: AnyAction) => {
  const storage = Storage.getInstance();

  switch (action.type) {
    case SIGN_OUT.FULFILLED:
      // We do not need to wait for confirmation from the server
      // to log us out
      storage.clear();
      return null;
    case GET_CURRENT_AUTHENTICATED_USER.FULFILLED:
    case UPDATE_CURRENT_AUTHENTICATED_USER:
      return action.payload;
    case GET_CURRENT_AUTHENTICATED_USER.REJECTED:
      return null;
    case STORE_PREFERRED_MFA_TYPE:
      return action.payload;
    default: {
      const [, requestType] = splitOnLastUnderscore(action.type || '');

      // Getting a 401 rejection should automatically log us out
      if (requestType === _REJECTED && get(action, 'payload.status') === UNAUTHORIZED) {
        storage.clear();
        return null;
      }
      return state;
    }
  }
};

/**
 * Updates the store state with the unauthenticated required info.
 * @param {AuthenticatorUser} state
 * @param {AnyAction} action
 * @return {AuthenticatorUser} new state
 */
export const unauthenticatedUser = (state: AuthenticatorUser = null, action: AnyAction): AuthenticatorUser => {
  switch (action.type) {
    case STORE_UNAUTHENTICATED_USER:
      return action.payload;
    default:
      return state;
  }
};
